/**
 * Shared pieces of the review desk.
 *
 * Rules held here rather than repeated at every call site:
 *  - Every state carries hue AND shape AND a glyph, so the whole system still reads
 *    when desaturated. Roughly one in twelve men has red-green colour blindness and
 *    this audience skews male in several of its source countries.
 *  - Provenance never borrows a verdict colour. "Verified" and "Not yet reviewed" sit
 *    on the same neutral chip and differ by dot fill, border style and words. A green
 *    "verified" badge would read as a reward and an amber "unreviewed" one as a warning,
 *    which would teach reviewers to distrust half the bank.
 *  - Icons come from @heroicons/react because it is already a project dependency. One
 *    icon family per project; no hand-drawn SVG paths.
 */
import React from 'react';
import {
  CheckIcon, XMarkIcon, FlagIcon, ExclamationTriangleIcon, MinusIcon,
} from '@heroicons/react/24/solid';
import { Provenance, PROVENANCE_LABELS } from '../../../inbde/reviewTypes';

export const Eyebrow: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`in-eyebrow ${className}`}>{children}</div>
);

export const ProvenanceChip: React.FC<{ value: Provenance; className?: string }> = ({ value, className = '' }) => {
  const variant: Record<Provenance, string> = {
    verified: 'in-chip--verified',
    ai_unreviewed: 'in-chip--unreviewed',
    in_review: 'in-chip--inreview',
    rejected: 'in-chip--rejected',
  };
  const solidDot = value === 'verified' || value === 'in_review';
  return (
    <span className={`in-chip ${variant[value]} ${className}`}>
      <span
        className={`in-chip__mark in-chip__mark--circle ${solidDot ? '' : 'in-chip__mark--hollow'}`}
        style={{ width: 8, height: 8, background: solidDot ? 'currentColor' : 'transparent' }}
      />
      {PROVENANCE_LABELS[value]}
    </span>
  );
};

export const FlaggedChip: React.FC = () => (
  <span className="in-chip in-chip--flagged">
    <FlagIcon className="w-3 h-3" aria-hidden="true" />
    Needs discussion
  </span>
);

export const KeyChip: React.FC = () => (
  <span className="in-chip in-chip--key">
    <CheckIcon className="w-3 h-3" aria-hidden="true" />
    Correct answer
  </span>
);

/** A labelled shortcut hint. Shown on every action so the keyboard path is discoverable. */
export const Kbd: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <kbd className="in-kbd">{children}</kbd>
);

export const Field: React.FC<{
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}> = ({ label, hint, error, children }) => (
  <label className="flex flex-col gap-2">
    <span className="in-eyebrow">{label}</span>
    {children}
    {hint && !error && (
      <span className="text-[11.5px] leading-snug" style={{ color: 'var(--text-tertiary)' }}>{hint}</span>
    )}
    {error && (
      <span className="text-[11.5px] leading-snug flex items-start gap-1.5" style={{ color: 'var(--in-incorrect)' }}>
        <ExclamationTriangleIcon className="w-3.5 h-3.5 flex-none mt-px" aria-hidden="true" />
        {error}
      </span>
    )}
  </label>
);

/** Skeletons take the shape of what is arriving, so nothing jumps when it lands. */
export const ItemSkeleton: React.FC = () => (
  <div className="flex flex-col gap-5" aria-busy="true" aria-label="Loading item">
    <div className="in-skeleton" style={{ height: 132 }} />
    <div className="in-skeleton" style={{ height: 22, width: '62%' }} />
    <div className="flex flex-col gap-2">
      {[0, 1, 2, 3].map((i) => <div key={i} className="in-skeleton" style={{ height: 52 }} />)}
    </div>
  </div>
);

export const QueueSkeleton: React.FC = () => (
  <div className="flex flex-col gap-1.5 p-3" aria-busy="true" aria-label="Loading queue">
    {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="in-skeleton" style={{ height: 62 }} />)}
  </div>
);

export const EmptyQueue: React.FC<{ filterLabel: string; onReset: () => void }> = ({ filterLabel, onReset }) => (
  <div className="flex flex-col items-center justify-center text-center px-8 py-16 gap-3">
    <CheckIcon className="w-7 h-7" style={{ color: 'var(--in-correct)' }} aria-hidden="true" />
    <div className="text-[15px] font-semibold" style={{ color: 'var(--text-primary)' }}>
      Nothing left in {filterLabel}
    </div>
    <p className="text-[13px] leading-relaxed max-w-[38ch]" style={{ color: 'var(--text-secondary)' }}>
      Every item in this filter has been dealt with. Switch filters to see the rest of the bank.
    </p>
    <button type="button" className="in-btn in-focus mt-1" onClick={onReset}>Show everything</button>
  </div>
);

export const ErrorNote: React.FC<{ message: string; onRetry?: () => void }> = ({ message, onRetry }) => (
  <div
    className="flex items-start gap-3 p-3.5 rounded"
    style={{
      background: 'var(--in-incorrect-bg)',
      border: '1px solid var(--in-incorrect-line)',
      borderRadius: 'var(--in-r-surface)',
    }}
    role="alert"
  >
    <ExclamationTriangleIcon className="w-4 h-4 flex-none mt-0.5" style={{ color: 'var(--in-incorrect)' }} aria-hidden="true" />
    <div className="flex-1">
      <div className="text-[13px] leading-relaxed" style={{ color: 'var(--in-incorrect)' }}>{message}</div>
      {onRetry && (
        <button type="button" className="in-btn in-focus mt-2.5" onClick={onRetry}>Try again</button>
      )}
    </div>
  </div>
);

export const StatusIcon: React.FC<{ value: Provenance; flagged: boolean }> = ({ value, flagged }) => {
  if (flagged) return <FlagIcon className="w-3.5 h-3.5" style={{ color: 'var(--in-flagged)' }} aria-hidden="true" />;
  if (value === 'verified') return <CheckIcon className="w-3.5 h-3.5" style={{ color: 'var(--in-correct)' }} aria-hidden="true" />;
  if (value === 'rejected') return <XMarkIcon className="w-3.5 h-3.5" style={{ color: 'var(--in-incorrect)' }} aria-hidden="true" />;
  if (value === 'in_review') return <MinusIcon className="w-3.5 h-3.5" style={{ color: 'var(--accent-primary)' }} aria-hidden="true" />;
  return <span className="w-3.5 h-3.5 inline-block rounded-full" style={{ border: '1.5px dashed var(--in-line-strong)' }} aria-hidden="true" />;
};
